/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-case-declarations */
/* eslint-disable react/jsx-sort-props */
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SendIcon from '@mui/icons-material/Send';
import { Box, Button, IconButton, Textarea } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import MenuOpen from '../../assets/images/MenuOpen.svg?react';
import { formatTimestamp } from '../../general-utilities/time-util';
import { MessageType, TerrainMessage, TextMessage } from '../../models/Message';
import { campaignRootStore } from '../../stores/CampaignRootStore';

type CampaignActivityFeedProps = {
  setViewSidebar: Dispatch<SetStateAction<boolean>>;
};

enum CampaignActivityItemType {
  Text,
  Action,
  Terrain
}

type CampaignActivityItemProps = {
  initials: string;
  username: string;
  type: CampaignActivityItemType;
  message: string;
  time: Date;
};

const CampaignActivityItem: React.FC<CampaignActivityItemProps> = ({ initials, username, type, message, time }) => {
  const { i18n } = useTranslation();

  return (
    <Box
      className='flex flex-row border-b'
      sx={{
        backgroundColor: type === CampaignActivityItemType.Action ? 'primary.softBg' : undefined,
        borderColor: 'divider'
      }}
    >
      <Box
        className='flex shrink-0 w-8 h-8 m-2 items-center justify-center font-bold text-base border-2 rounded-2xl'
        sx={{
          backgroundColor: 'primary.solidColor',
          borderColor: 'divider'
        }}
      >
        {initials.toUpperCase()}
      </Box>
      <Box className='flex flex-col flex-1 mr-2 justify-start'>
        <Box className='flex flex-row w-full items-start mt-2 justify-between'>
          {type === CampaignActivityItemType.Text ? (
            <Box className='flex flex-col w-full items-start justify-start overflow-hidden'>
              <Box>{username}</Box>
              <Box>{message}</Box>
            </Box>
          ) : (
            <Box className='flex flex-row w-full items-start justify-start overflow-hidden'>{message /* TODO */}</Box>
          )}
          <Box>{formatTimestamp(time.toString(), i18n.language, { onlyHourAndMinute: true })}</Box>
        </Box>
      </Box>
    </Box>
  );
};

type CampaignActivityInfo = {
  initials: string;
  username: string;
  type: CampaignActivityItemType;
  message: string;
  time: Date;
};

const CampaignActivityFeed: React.FC<CampaignActivityFeedProps> = observer(({ setViewSidebar }) => {
  const { t } = useTranslation();
  const { messageStore } = campaignRootStore;

  const messagesDivRef = useRef<HTMLDivElement>(null);

  const [messageText, setMessageText] = useState<string>('');

  const campaignActivities: CampaignActivityInfo[] = messageStore.getReceivedMessages().map((message) => {
    switch (message.messageType) {
      case MessageType.Freetext:
        const textMessage = message as TextMessage;
        return {
          initials: textMessage.user!.initials,
          username: textMessage.user!.userName,
          type: CampaignActivityItemType.Text,
          message: textMessage.text,
          time: new Date(message.timeStamp)
        };
      case MessageType.UserJoinedMission:
        return {
          initials: message.user!.initials,
          username: message.user!.userName,
          type: CampaignActivityItemType.Action,
          message: `${message.user!.userName} joined the mission.`,
          time: new Date(message.timeStamp)
        };
      case MessageType.UserLeftMission:
        return {
          initials: message.user!.initials,
          username: message.user!.userName,
          type: CampaignActivityItemType.Action,
          message: `${message.user!.userName} left the mission.`,
          time: new Date(message.timeStamp)
        };
      case MessageType.AssumeLiveViewControls:
        return {
          initials: message.user!.initials,
          username: message.user!.userName,
          type: CampaignActivityItemType.Action,
          message: `${message.user!.userName} assumed live view controls.`,
          time: new Date(message.timeStamp)
        };
      case MessageType.ReleaseLiveViewControls:
        return {
          initials: message.user!.initials,
          username: message.user!.userName,
          type: CampaignActivityItemType.Action,
          message: `${message.user!.userName} released live view controls.`,
          time: new Date(message.timeStamp)
        };
      case MessageType.CropInspectionNeeded:
        /* TODO */
        const terrainMessage = message as TerrainMessage;
        return {
          initials: terrainMessage.user!.initials,
          username: terrainMessage.user!.userName,
          type: CampaignActivityItemType.Terrain,
          message: terrainMessage.text,
          time: new Date(message.timeStamp)
        };
      default:
        throw new Error('Invalid MessageType');
    }
  });

  const sendMessage = useCallback(() => {
    const trimmed = messageText.trim();
    if (!trimmed) return;
    messageStore.setMessageToBeSent({
      messageType: MessageType.Freetext,
      text: trimmed
    } as TextMessage);
    setMessageText('');
  }, [messageText, messageStore]);

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') {
      sendMessage();
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (messagesDivRef.current) {
      messagesDivRef.current.scrollTop = messagesDivRef.current.scrollHeight;
    }
  }, [campaignActivities]);

  return (
    <Box className='w-[15%] h-full overflow-auto mr-4'>
      <Box
        className='h-[8%] flex flex-row px-4 py-3 items-center justify-between border-y'
        sx={{
          borderColor: 'divider'
        }}
      >
        Mission Activity Feed
        <Button
          sx={{
            display: 'flex',
            width: '50px',
            height: '50px',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.body',
            '&:hover': {
              backgroundColor: 'background.body'
            },
            borderRadius: '0px'
          }}
          onClick={() => setViewSidebar((prev) => !prev)}
        >
          <Box
            sx={{
              display: 'flex',
              width: '30px',
              height: '30px',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MenuOpen
              style={{
                stroke: 'text/icon'
              }}
            />
          </Box>
        </Button>
      </Box>
      <Box className='h-[92%] flex flex-col justify-between'>
        <Box ref={messagesDivRef} className='overflow-auto'>
          {campaignActivities.map((ca) => (
            <CampaignActivityItem
              key={`${ca.username}${ca.time.toISOString()}`}
              type={ca.type}
              initials={ca.initials}
              username={ca.username}
              message={ca.message}
              time={ca.time}
            />
          ))}
        </Box>
        <Box className='flex flex-row w-full'>
          <Textarea
            className='w-full pr-5'
            aria-label='Message to be sent'
            placeholder={t('campaign_view.write_your_message')}
            maxRows={4}
            value={messageText}
            onChange={(event) => setMessageText(event.target.value)}
            onKeyDown={handleKeyDown}
          />
          <IconButton
            aria-label='Send message'
            size='sm'
            variant='plain'
            sx={{ marginLeft: '-35px' }}
            disabled={!messageText.trim()}
            onClick={sendMessage}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
});

export default CampaignActivityFeed;
